/**
 * PHARMAKON — chart primitives (SVG, zero external deps)
 *
 * Ported from the design's components/charts/Charts.jsx as proper TSX
 * modules. Sparkline, LineChart, BarChart, DonutChart, TrendIndicator — plus
 * ColumnChart (batch-1 §2), the vertical, multi-série, axée-et-graduée surface
 * du graphique multi-paramètres.
 *
 * Toutes les couleurs sortent des JETONS du système de conception
 * (`var(--color-…)`) : aucune valeur hexadécimale n'est écrite ici, donc les
 * trois thèmes (clair / sombre / Novera) suivent sans une ligne de plus.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/primitives";

// ============================================================
// Sparkline — small inline trend
// ============================================================
export interface SparklineProps {
  data?: number[];
  width?: number;
  height?: number;
  color?: string;
  area?: boolean;
  strokeWidth?: number;
  className?: string;
}
export const Sparkline: React.FC<SparklineProps> = ({
  data = [],
  width = 100,
  height = 28,
  color = "currentColor",
  area = true,
  strokeWidth = 1.5,
  className = "",
}) => {
  if (!data || data.length < 2)
    return <svg width={width} height={height} className={className}></svg>;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i): [number, number] => [
    i * stepX,
    height - ((v - min) / range) * height,
  ]);
  const line = pts
    .map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(" ");
  const areaPath = `${line} L${width},${height} L0,${height} Z`;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
    >
      {area && <path d={areaPath} fill={color} opacity="0.12" />}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ============================================================
// LineChart — interactive with tooltip
// ============================================================
export interface LineChartProps {
  data?: number[];
  labels?: string[];
  height?: number;
  color?: string;
  yFormatter?: (v: number) => React.ReactNode;
}
export const LineChart: React.FC<LineChartProps> = ({
  data = [],
  labels = [],
  height = 220,
  color = "var(--color-accent)",
  yFormatter = (v) => v,
}) => {
  const [hover, setHover] = React.useState<
    { i: number; v: number; x: number; y: number } | null
  >(null);
  const padding = { top: 16, right: 8, bottom: 24, left: 44 };
  const width = 600;
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const max = Math.max(...data, 0);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = data.length > 1 ? innerW / (data.length - 1) : innerW;
  const yToPx = (v: number) =>
    padding.top + innerH - ((v - min) / range) * innerH;
  const xToPx = (i: number) => padding.left + i * stepX;
  const linePath = data
    .map(
      (v, i) =>
        `${i ? "L" : "M"}${xToPx(i).toFixed(1)},${yToPx(v).toFixed(1)}`,
    )
    .join(" ");
  const areaPath =
    data.length > 0
      ? `${linePath} L${xToPx(data.length - 1)},${padding.top + innerH} L${padding.left},${padding.top + innerH} Z`
      : "";

  const yTicks = 4;
  const yValues = Array.from(
    { length: yTicks + 1 },
    (_, i) => min + (range * i) / yTicks,
  );

  return (
    <div className="relative w-full">
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        className="block"
        onMouseLeave={() => setHover(null)}
      >
        {yValues.map((v, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yToPx(v)}
              y2={yToPx(v)}
              stroke="var(--color-border-subtle)"
              strokeDasharray="2 3"
            />
            <text
              x={padding.left - 6}
              y={yToPx(v) + 3}
              textAnchor="end"
              fontSize="9"
              fill="var(--color-text-muted)"
              className="font-mono"
            >
              {yFormatter(Math.round(v))}
            </text>
          </g>
        ))}
        {labels.map(
          (l, i) =>
            i % Math.ceil(labels.length / 8) === 0 && (
              <text
                key={i}
                x={xToPx(i)}
                y={height - 6}
                textAnchor="middle"
                fontSize="9"
                fill="var(--color-text-muted)"
              >
                {l}
              </text>
            ),
        )}
        <path d={areaPath} fill={color} opacity="0.08" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((v, i) => (
          <g key={i}>
            <rect
              x={xToPx(i) - stepX / 2}
              y={padding.top}
              width={stepX}
              height={innerH}
              fill="transparent"
              onMouseEnter={() =>
                setHover({ i, v, x: xToPx(i), y: yToPx(v) })
              }
            />
            {hover?.i === i && (
              <>
                <line
                  x1={xToPx(i)}
                  x2={xToPx(i)}
                  y1={padding.top}
                  y2={padding.top + innerH}
                  stroke="var(--color-text-muted)"
                  strokeDasharray="2 3"
                  opacity="0.6"
                />
                <circle
                  cx={xToPx(i)}
                  cy={yToPx(v)}
                  r="4"
                  fill="var(--color-bg-elevated)"
                  stroke={color}
                  strokeWidth="2"
                />
              </>
            )}
          </g>
        ))}
      </svg>
      {hover && (
        <div
          className="absolute pointer-events-none px-2.5 py-1.5 rounded-md text-2xs font-mono"
          style={{
            background: "var(--color-bg-elevated)",
            border: "1px solid var(--color-border-default)",
            left: `${(hover.x / width) * 100}%`,
            top: `${(hover.y / height) * 100}%`,
            transform: "translate(-50%, -110%)",
            boxShadow: "0 4px 12px rgb(0 0 0 / 0.08)",
          }}
        >
          <div className="text-text-muted">
            {labels[hover.i] || `J-${data.length - hover.i}`}
          </div>
          <div className="text-text-strong">{yFormatter(hover.v)}</div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BarChart — horizontal, for top products
// ============================================================
export interface BarChartDatum {
  label: string;
  value: number;
  sub?: string;
}
export interface BarChartProps {
  data?: BarChartDatum[];
  height?: number;
  valueFormatter?: (v: number) => React.ReactNode;
  color?: string;
}
export const BarChart: React.FC<BarChartProps> = ({
  data = [],
  valueFormatter = (v) => v,
  color = "var(--color-accent)",
}) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex flex-col gap-2">
      {data.map((d, i) => (
        <div key={i} className="grid grid-cols-[1fr,auto] gap-3 items-center">
          <div className="min-w-0">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs text-text-default truncate">
                {d.label}
              </span>
              <span className="text-xs font-mono text-text-muted ml-2">
                {valueFormatter(d.value)}
              </span>
            </div>
            <div className="h-2 bg-bg-sunken rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-med"
                style={{
                  width: `${(d.value / max) * 100}%`,
                  background: color,
                }}
              ></div>
            </div>
          </div>
          {d.sub && (
            <span className="text-2xs text-text-muted font-mono">{d.sub}</span>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// DonutChart
// ============================================================
export interface DonutDatum {
  label: string;
  value: number;
  color?: string;
}
export interface DonutChartProps {
  data?: DonutDatum[];
  size?: number;
  thickness?: number;
  centerLabel?: React.ReactNode;
  centerValue?: React.ReactNode;
}
export const DonutChart: React.FC<DonutChartProps> = ({
  data = [],
  size = 180,
  thickness = 22,
  centerLabel,
  centerValue,
}) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const cx = size / 2;
  const cy = size / 2;
  return (
    <div className="flex items-center gap-5">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--color-bg-sunken)"
          strokeWidth={thickness}
        />
        {data.map((d, i) => {
          const len = (d.value / total) * c;
          const seg = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={`var(--color-${d.color || "accent"})`}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return seg;
        })}
      </svg>
      <div className="flex flex-col gap-1.5 min-w-0">
        {centerLabel && (
          <div className="mb-1">
            <div className="text-xs text-text-muted">{centerLabel}</div>
            <div className="text-2xl font-semibold kpi-num text-text-strong">
              {centerValue ?? total}
            </div>
          </div>
        )}
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: `var(--color-${d.color || "accent"})` }}
            ></span>
            <span className="text-text-default whitespace-nowrap">
              {d.label}
            </span>
            <span className="text-text-muted font-mono ml-auto">
              {d.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// ColumnChart — barres VERTICALES, multi-séries, avec de vrais axes
// (batch-1 §2 — le graphique multi-paramètres)
// ============================================================
//
// Ce que `BarChart` ci-dessus ne sait pas faire, et pourquoi il fallait un
// second composant plutôt qu'une option de plus :
//
//   • BarChart est un CLASSEMENT (barres horizontales, une série, pas d'axe des
//     ordonnées). Il répond à « qui est en tête ». On le garde tel quel.
//   • ColumnChart est une MESURE : axe des ordonnées gradué, grille horizontale
//     discrète, deux séries comparables côte à côte (période courante /
//     précédente), et un axe des abscisses qui peut porter des dates.
//
// Décisions tenues ici, toutes vérifiables à l'œil :
//   – aucune couleur en dur : les séries portent un JETON (`accent`, `info`,
//     `neutral`) résolu en `var(--color-…)` ;
//   – la légende n'apparaît qu'à partir de DEUX séries (une légende à une
//     entrée est du bruit) ;
//   – les valeurs sont formatées par l'appelant (fr-DZ, DA) : le composant ne
//     connaît ni la monnaie ni la locale ;
//   – données vides → le graphique ne se rend PAS à moitié ; l'appelant montre
//     son état vide. Une valeur non finie est traitée comme 0 plutôt que de
//     produire un `path` en `NaN` (qui fait disparaître le tracé entier) ;
//   – une colonne cliquable est un vrai bouton clavier (`<g role="button">`
//     focusable), parce que la descente ne doit pas être réservée à la souris.

const JETON_VERS_VAR: Record<string, string> = {
  accent: "var(--color-accent)",
  info: "var(--color-info)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  danger: "var(--color-danger)",
  // La série de comparaison est délibérément NEUTRE : la période précédente est
  // un repère, pas un protagoniste. Elle ne doit jamais rivaliser avec l'accent.
  neutral: "var(--color-border-strong)",
};

/** Un jeton de série → sa variable CSS. Inconnu → l'accent, jamais du noir. */
export const couleurSerie = (jeton?: string) =>
  JETON_VERS_VAR[jeton ?? "accent"] ?? JETON_VERS_VAR.accent;

/** Une valeur qui n'est pas un nombre fini vaut 0 — jamais `NaN` dans un `path`. */
const sur = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/**
 * Graduations « rondes » pour l'axe des ordonnées.
 *
 * Un axe gradué 0 / 3 333 / 6 667 / 10 000 est illisible ; on monte au pas
 * rond supérieur (1 / 2 / 2,5 / 5 × 10ⁿ) pour que les repères soient des
 * nombres qu'un humain retient.
 */
export function graduations(max: number, nb = 4): number[] {
  const m = sur(max);
  if (m <= 0) return [0, 1];
  const brut = m / nb;
  const magnitude = Math.pow(10, Math.floor(Math.log10(brut)));
  const norm = brut / magnitude;
  const pas = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude;
  const out: number[] = [];
  // L'échelle doit COUVRIR le maximum, pas s'arrêter juste en dessous : un axe
  // qui plafonne à 6 pour une barre de 7 laisse la barre dépasser du cadre.
  // On monte donc au multiple du pas immédiatement supérieur ou égal.
  const haut = Math.ceil(m / pas - 1e-9) * pas;
  for (let i = 0; i <= Math.round(haut / pas); i++) {
    out.push(Math.round(i * pas * 1e6) / 1e6);
  }
  if (out.length < 2) out.push(pas);
  return out;
}

export interface ColumnSerie {
  cle: string;
  libelle: string;
  /** Jeton du système de conception (`accent`, `info`, `neutral`, …). */
  jeton?: string;
  /** X XWP-24 — le compte affiché dans la légende à côté du libellé (« Libre · 128 »). */
  compte?: string;
}
export interface ColumnDatum {
  cle: string;
  label: string;
  /** Une valeur par série, dans l'ordre de `series`. */
  valeurs: number[];
  /** Info libre affichée dans l'infobulle (part du total, delta…). */
  note?: string;
}
export interface ColumnChartProps {
  data?: ColumnDatum[];
  series?: ColumnSerie[];
  height?: number;
  /** Formate une valeur pour l'infobulle. */
  valueFormatter?: (v: number) => string;
  /** Formate une graduation de l'axe des ordonnées (souvent plus court). */
  axisFormatter?: (v: number) => string;
  /** Descente : appelée au clic ou à `Entrée` sur une colonne. */
  onSelect?: (d: ColumnDatum) => void;
  /** Libellé accessible de l'action de descente. */
  selectLabel?: (d: ColumnDatum) => string;
  /**
   * X XWP-24 — colonnes EMPILÉES : une barre par catégorie, ses séries posées l'une sur l'autre dans
   * l'ordre de `series` (Libre, CHIFA, Instances) ; la hauteur de la barre est leur somme.
   */
  empile?: boolean;
  /**
   * X XWP-24 — le haut d'échelle imposé : plusieurs petits graphiques côte à côte partagent la MÊME
   * échelle (sinon deux barres de même hauteur diraient deux nombres différents).
   */
  echelleMax?: number;
  /** Libellé du total dans l'infobulle d'une colonne empilée. */
  totalLabel?: string;
  className?: string;
}

/**
 * Le haut de l'échelle d'un `ColumnChart` : la plus grande barre — la somme des séries d'une catégorie
 * quand elles sont empilées, la plus grande valeur sinon — ou l'échelle imposée si elle est plus haute.
 */
export function maxColonnes(data: ColumnDatum[] = [], { empile = false, echelleMax }: { empile?: boolean; echelleMax?: number } = {}) {
  const barres = data.map((d) =>
    empile
      ? (d.valeurs ?? []).reduce((s, v) => s + sur(v), 0)
      : Math.max(0, ...(d.valeurs ?? []).map((v) => sur(v))),
  );
  return Math.max(0, sur(echelleMax), ...barres);
}

export const ColumnChart: React.FC<ColumnChartProps> = ({
  data = [],
  series = [{ cle: "v", libelle: "Valeur", jeton: "accent" }],
  height = 260,
  valueFormatter = (v) => String(v),
  axisFormatter,
  onSelect,
  selectLabel,
  empile = false,
  echelleMax,
  totalLabel = "Total",
  className = "",
}) => {
  const [survol, setSurvol] = React.useState<number | null>(null);
  // Avant tout retour anticipé : un hook appelé conditionnellement casse l'ordre des hooks dès que
  // `data` passe de vide à rempli.
  const idLegende = React.useId();
  const fmtAxe = axisFormatter ?? valueFormatter;

  // Repère. La marge gauche laisse la place aux montants en dinars ; la marge
  // basse, aux libellés inclinés quand ils sont nombreux.
  const width = 720;
  const nbCat = data.length;
  const labelsSerres = nbCat > 8;
  const pad = { top: 14, right: 10, bottom: labelsSerres ? 58 : 34, left: 62 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const maxBrut = maxColonnes(data, { empile, echelleMax });
  const ticks = graduations(maxBrut);
  const maxEchelle = ticks[ticks.length - 1] || 1;
  const yPx = (v: number) => pad.top + innerH - (sur(v) / maxEchelle) * innerH;

  const pasCat = nbCat > 0 ? innerW / nbCat : innerW;
  // Une colonne large se lit mieux qu'un fil : on plafonne la largeur du groupe
  // à 56 px pour éviter les « murs » quand il n'y a que deux catégories.
  const largeurGroupe = Math.min(pasCat * 0.68, 56 * (empile ? 1 : series.length));
  const largeurBarre = empile ? Math.max(3, largeurGroupe) : Math.max(3, largeurGroupe / Math.max(1, series.length));
  const totalDe = (d: ColumnDatum) => (d.valeurs ?? []).reduce((s, v) => s + sur(v), 0);

  if (!nbCat) return null;

  return (
    <div className={cn("relative w-full", className)}>
      {series.length > 1 && (
        <ul
          id={idLegende}
          className="flex flex-wrap items-center gap-4 px-1 pb-2"
          aria-label="Légende du graphique"
        >
          {series.map((s) => (
            <li key={s.cle} className="flex items-center gap-1.5 text-xs text-text-default">
              <span
                aria-hidden="true"
                className="inline-block w-2.5 h-2.5 rounded-[2px] shrink-0"
                style={{ background: couleurSerie(s.jeton) }}
              />
              {s.libelle}
              {s.compte != null && (
                <span className="font-mono kpi-num text-text-strong">{s.compte}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        className="block overflow-visible"
        role="img"
        aria-describedby={series.length > 1 ? idLegende : undefined}
        onMouseLeave={() => setSurvol(null)}
      >
        {/* Grille + axe des ordonnées — discrète : elle guide l'œil, elle ne
            dessine pas un quadrillage de cahier. */}
        {ticks.map((t, i) => (
          <g key={`t${i}`}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={yPx(t)}
              y2={yPx(t)}
              stroke="var(--color-border-subtle)"
              strokeDasharray={i === 0 ? undefined : "2 4"}
            />
            <text
              x={pad.left - 8}
              y={yPx(t) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--color-text-muted)"
              className="font-mono kpi-num"
            >
              {fmtAxe(t)}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const x0 = pad.left + i * pasCat + (pasCat - largeurGroupe) / 2;
          const actif = survol === i;
          const cliquable = !!onSelect;
          return (
            <g
              key={d.cle ?? i}
              role={cliquable ? "button" : undefined}
              tabIndex={cliquable ? 0 : undefined}
              aria-label={
                cliquable && selectLabel
                  ? selectLabel(d)
                  : empile
                    ? `${d.label} : ${series
                        .map((s, j) => `${s.libelle} ${valueFormatter(sur(d.valeurs?.[j]))}`)
                        .join(", ")} ; ${totalLabel} ${valueFormatter(totalDe(d))}`
                    : `${d.label} : ${valueFormatter(sur(d.valeurs?.[0]))}`
              }
              className={cn(
                cliquable && "cursor-pointer focus:outline-none",
                "[&:focus-visible>rect.zone]:stroke-accent [&:focus-visible>rect.zone]:stroke-2",
              )}
              onMouseEnter={() => setSurvol(i)}
              onFocus={() => setSurvol(i)}
              onBlur={() => setSurvol(null)}
              onClick={cliquable ? () => onSelect?.(d) : undefined}
              onKeyDown={
                cliquable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect?.(d);
                      }
                    }
                  : undefined
              }
            >
              {/* Zone de survol/cible — pleine hauteur, ≥ 44 px de large dès que
                  la place le permet : on ne vise pas une barre de 4 px. */}
              <rect
                className="zone"
                x={pad.left + i * pasCat}
                y={pad.top}
                width={pasCat}
                height={innerH}
                fill={actif ? "var(--color-accent-soft)" : "transparent"}
                opacity={actif ? 0.35 : 1}
                rx={4}
              />
              {empile
                ? (() => {
                    // Les segments se posent du bas vers le haut, dans l'ordre des séries ; un
                    // segment nul n'est pas dessiné (pas de liseré trompeur).
                    let base = 0;
                    return series.map((s, j) => {
                      const v = sur(d.valeurs?.[j]);
                      if (v <= 0) return null;
                      const yHaut = yPx(base + v);
                      const yBas = yPx(base);
                      base += v;
                      return (
                        <rect
                          key={s.cle}
                          x={x0}
                          y={yHaut}
                          width={Math.max(2, largeurBarre - 2)}
                          height={Math.max(1, yBas - yHaut)}
                          fill={couleurSerie(s.jeton)}
                          stroke="var(--color-bg-elevated)"
                          strokeWidth={1}
                          opacity={survol == null || actif ? 1 : 0.55}
                        />
                      );
                    });
                  })()
                : series.map((s, j) => {
                    const v = sur(d.valeurs?.[j]);
                    const h = Math.max(v > 0 ? 2 : 0, pad.top + innerH - yPx(v));
                    return (
                      <rect
                        key={s.cle}
                        x={x0 + j * largeurBarre}
                        y={pad.top + innerH - h}
                        width={Math.max(2, largeurBarre - 2)}
                        height={h}
                        rx={2}
                        fill={couleurSerie(s.jeton)}
                        opacity={survol == null || actif ? 1 : 0.55}
                      />
                    );
                  })}
              <text
                x={pad.left + i * pasCat + pasCat / 2}
                y={height - (labelsSerres ? 34 : 12)}
                textAnchor={labelsSerres ? "end" : "middle"}
                transform={
                  labelsSerres
                    ? `rotate(-38 ${pad.left + i * pasCat + pasCat / 2} ${height - 34})`
                    : undefined
                }
                fontSize="11"
                fill={actif ? "var(--color-text-strong)" : "var(--color-text-muted)"}
              >
                {d.label.length > 18 ? `${d.label.slice(0, 17)}…` : d.label}
              </text>
            </g>
          );
        })}
      </svg>

      {survol != null && data[survol] && (
        <div
          className="absolute pointer-events-none px-3 py-2 rounded-md shadow-pop"
          style={{
            background: "var(--color-bg-elevated)",
            border: "1px solid var(--color-border-default)",
            left: `${((pad.left + survol * pasCat + pasCat / 2) / width) * 100}%`,
            top: 0,
            transform: "translate(-50%, -6px)",
            minWidth: 150,
          }}
          role="status"
        >
          <div className="text-xs font-semibold text-text-strong mb-1">
            {data[survol].label}
          </div>
          {series.map((s, j) => (
            <div key={s.cle} className="flex items-center justify-between gap-4 text-xs">
              <span className="flex items-center gap-1.5 text-text-muted">
                <span
                  aria-hidden="true"
                  className="inline-block w-2 h-2 rounded-[2px]"
                  style={{ background: couleurSerie(s.jeton) }}
                />
                {s.libelle}
              </span>
              <span className="font-mono kpi-num text-text-strong">
                {valueFormatter(sur(data[survol].valeurs?.[j]))}
              </span>
            </div>
          ))}
          {empile && (
            <div className="flex items-center justify-between gap-4 text-xs mt-1 pt-1 border-t border-border-subtle">
              <span className="text-text-muted">{totalLabel}</span>
              <span className="font-mono kpi-num text-text-strong">
                {valueFormatter(totalDe(data[survol]))}
              </span>
            </div>
          )}
          {data[survol].note && (
            <div className="mt-1 pt-1 border-t border-border-subtle text-2xs text-text-muted">
              {data[survol].note}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// TrendIndicator
// ============================================================
export interface TrendIndicatorProps {
  delta?: number | null;
  suffix?: string;
  neutralAtZero?: boolean;
  className?: string;
}
export const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  delta,
  suffix = "%",
  neutralAtZero = true,
  className = "",
}) => {
  if (delta == null) return null;
  const isUp = delta > 0;
  const isDown = delta < 0;
  const tone =
    neutralAtZero && delta === 0
      ? "text-text-muted"
      : isUp
        ? "text-success"
        : "text-danger";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-mono font-medium",
        tone,
        className,
      )}
    >
      <Icon
        name={isUp ? "arrow-up-right" : isDown ? "arrow-down-right" : "minus"}
        size={11}
      />
      {isUp && "+"}
      {delta.toFixed(1)}
      {suffix}
    </span>
  );
};
